// FIX #3 APPLIED
import { lazy, Suspense, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { Observer } from 'gsap/Observer'
// Type-only — `import type` guarantees this never becomes a runtime import and
// so never drags Three.js back into the main bundle. See CinematicScene below.
import type { SceneView } from '../components/atmosphere/CinematicScene'
import { InstagramIcon } from '../components/common/SocialIcons'
import { OfferTicker, TICKER_HEIGHT, TICKER_HEIGHT_MOBILE } from '../components/offers/OfferTicker'
import { OffersHighlight } from '../components/offers/OffersHighlight'
import { prefersReducedMotion } from '../utils/motion'
import { trackCtaClick, trackScrollDepth } from '../utils/analytics'

// The Collection band is below several full-screen sections, and it is the only
// part of this page that talks to the catalogue. Splitting it into its own chunk
// keeps its code, its fetch and its imagery off the critical path entirely — see
// DeferredCollection, which mounts it as the visitor approaches.
const CollectionSection = lazy(() => import('../components/landing/CollectionSection'))

// Social proof, code-split for the same reason: it sits just above the footer
// CTA, fetches its own quotes, and hides itself outright when there are none.
const Testimonials = lazy(() => import('../components/landing/Testimonials'))

// The 3D stage is split out for one reason: Landing is the ONE eager route
// (App.tsx), so a static import here put all of Three.js in the main bundle,
// where the browser had to download and evaluate it before React could mount —
// several seconds of blank screen BEFORE the intro loader could even appear.
// Behind lazy() it downloads as its own chunk, in parallel, while the loader is
// already on screen. Nothing waits on it: the loader is a fixed 2.2s timeline
// (see the reveal effect) and the scene fades in underneath whenever it lands.
const CinematicScene = lazy(() => import('../components/atmosphere/CinematicScene'))

gsap.registerPlugin(ScrollTrigger, Observer)

// Scroll-depth milestones, in ascending order. `as const` is load-bearing: it
// is what narrows the element type to trackScrollDepth's 25 | 50 | 75 | 100.
const SCROLL_THRESHOLDS = [25, 50, 75, 100] as const

// The honest differentiator — off-the-shelf vs. composed-for-you. Every line
// here is true of the actual product; no invented reviews, no invented claims.
const COMPARE = [
  {
    tag: 'Any designer bottle',
    mark: '—',
    points: [
      // No scale claims in either column — ours would be untrue at launch, and
      // an implied "everyone wears theirs" only invites the same question back.
      'Composed for a global average, not for anyone in particular.',
      'Chosen by trial and error at a crowded counter.',
      'Smells like the person standing next to you.',
      'A logo on the label.',
    ],
  },
  {
    tag: 'The Perfume Culture',
    mark: '✦',
    points: [
      'Composed from your answers — no one else’s.',
      'Every note shown before you decide.',
      'Smells like no one but you.',
      'Your story on the label.',
    ],
  },
]

// Objection-handling FAQ — logistics only. "How it works" and "not ready?"
// live in their own sections above; repeating them here was noise.
const FAQ = [
  { q: 'What exactly do I receive?', a: 'Your chosen formula as a 50 ml Eau de Parfum — weighted glass, cream label, black cap — in the kraft-and-navy canister it was designed to live in.' },
  { q: 'How long until it reaches me?', a: 'Your composition appears in minutes. Once you order, we blend, bottle and ship within 7-10 working days — shipping is free across India.' },
  { q: 'How do I pay?', a: 'Securely online at checkout — UPI, cards, netbanking or wallets, via Cashfree.' },
]

// Anatomy of a scent — the "what's inside" transparency beat.
const NOTES = [
  { icon: 'citrus', tag: 'Top Notes',  t: 'The first breath', d: 'Bright and fleeting — citrus, spice, cut green. The hello that turns heads in the first ten minutes.' },
  { icon: 'flower', tag: 'Heart Notes', t: 'The soul',         d: 'Florals, woods and warmth that carry your story for hours. This is the part people remember as you.' },
  { icon: 'mist',   tag: 'Base Notes', t: 'The memory',       d: 'Musk, amber, soft resins — what lingers on collars, scarves and skin long after you’ve gone.' },
]

// Section numbering removed — the rows read in order without being counted.
const HOW_STEPS = [
  { t: 'Answer ten questions', d: 'About two minutes in the studio — who you are at your core, how you want to be remembered, what smell feels like home.' },
  { t: 'Choose your composition', d: 'Personality mapping translates your answers into two complete formulations — a primary and an alternative — with every top, heart and base note named. The exact proportions stay our atelier’s secret. You pick the one that feels like you.' },
  { t: 'We blend, bottle & ship', d: 'Your formula is blended in our Indian atelier, bottled as a 50 ml Eau de Parfum, and shipped free to your door.' },
]

// Scrolling brand line between the journey and the editorial sections —
// the Hungry Tiger-style marquee. Copy lifted from the packaging.
const MARQUEE = ['Crafted for you', 'Inspired by you', 'Defined by you', 'Designed to be remembered']


// The editorial "From Atelier to You" band that used to sit above the
// collection was removed: it was a second three-card rail immediately before
// the product rail, showing the same kind of imagery with none of the product
// information, and it made the two read as duplicates of each other.

// ── Journey steps ────────────────────────────────────────────────────────────
// One gesture = one step. Each step owns a camera view; together they trace a
// full 360° flight around the bottle (az sweeps ~0 → 2π), ending front again.
// Pure storytelling — the questionnaire lives in the studio (/craft) now.

// The locked journey is now pure poster beats — one short statement per screen
// over the bottle, nothing else. Cards, notes and how-it-works all live in the
// free-scroll editorial below, where they can breathe.
type Step =
  | { k: 'hero'; view: SceneView }
  | { k: 'portal'; view: SceneView }
  | { k: 'text'; eyebrow: string; title: string; sub?: string; view: SceneView }
  | { k: 'finale'; view: SceneView }

const V = (az: number, radius: number, camY: number, lookY: number, portal = 0, capOpen = 0): SceneView =>
  ({ az, radius, camY, lookY, portal, capOpen })

// Copy lifted from the real packaging: "Designed to be remembered", "Your scent
// reflection is here", "Crafted for you. Inspired by you. Defined by you.",
// personality-mapping. The site speaks exactly like the box does.
const STEPS: Step[] = [
  { k: 'hero',   view: V(-0.10, 8.6, 1.50, 1.05) },
  { k: 'portal', view: V(0.00, 4.9, 0.78, 0.60, 1) },
  { k: 'text',   eyebrow: 'Fragrance Is Memory', title: 'You are remembered as *a feeling.*', view: V(1.05, 7.8, 2.30, 1.15) },
  // The showpiece: the cap lifts off, the atomizer fires AT the viewer, and the
  // mist beads up on the screen like rain on a window.
  //
  // Head-on is deliberate — the burst comes out of the screen. Two things have
  // to be true for that to read as spray rather than as a puff of smoke, and
  // both live in CinematicScene: the droplets must dissolve before they reach
  // the lens (sizeAttenuation inflates a near sprite into a screen-filling
  // blob), and the cone must expand as it approaches, which is how a cone
  // seen along its own axis expresses itself. See the near-lens fade there.
  { k: 'text',   eyebrow: 'The First Press', title: 'One press — *the air changes.*', sub: 'A fine, weightless mist. That is all it takes.', view: V(0.05, 6.2, 1.60, 1.30, 0, 1) },
  { k: 'text',   eyebrow: 'Our Promise', title: 'Crafted for you. Inspired by you. *Defined by you.*', sub: 'Ten thousand people can buy the same designer bottle. Nobody can buy yours.', view: V(3.60, 8.4, 2.40, 1.15) },
  { k: 'text',   eyebrow: 'Sillage', title: 'It stays *after you leave.*', sub: 'Perfumers call it sillage — the trail your scent leaves in a room.', view: V(4.85, 7.2, 1.00, 0.95) },
  { k: 'finale', view: V(6.18, 8.6, 1.50, 1.05) },
]

// On-screen diameter (px) of the 3D cap in the top-down view — keeps the CSS
// loader ring aligned with the real cap so it "becomes" it. Mirrors CinematicScene:
// camera 6.5 above + 0.4 offset, fov 30, cap barrel radius 0.44 × group scale.
function capRingPx(): number {
  if (typeof window === 'undefined') return 130
  const coarse = window.matchMedia?.('(pointer: coarse)').matches ?? false
  const scl = coarse && window.innerWidth < 700 ? 0.82 : 0.95
  const R = 0.44 * scl
  const dist = Math.sqrt(6.5 * 6.5 + 0.4 * 0.4)
  const px = (R / (dist * Math.tan((30 * Math.PI) / 360))) * window.innerHeight
  return Math.max(80, Math.min(260, px))
}

// Line icons used by the story cards (notes anatomy, craft section).
function OptIcon({ name }: { name: string }) {
  let body: React.ReactNode
  switch (name) {
    case 'spark':  body = <path d="M12 3l1.7 5.1 5.3 1.6-5.3 1.6L12 21l-1.7-5.1L5 14.3l5.3-1.6z" />; break
    case 'gem':    body = <><path d="M5 4h14l3 5-10 12L2 9z" /><path d="M2 9h20" /><path d="M9 4 7 9l5 12 5-12-2-5" /></>; break
    case 'citrus': body = <><circle cx="12" cy="12" r="9" /><path d="M12 3v18M3 12h18M5.6 5.6l12.8 12.8M18.4 5.6 5.6 18.4" /></>; break
    case 'flower': body = <>{[0, 60, 120, 180, 240, 300].map((a) => <ellipse key={a} cx="12" cy="7.5" rx="2.3" ry="3.8" transform={`rotate(${a} 12 12)`} />)}<circle cx="12" cy="12" r="2" /></>; break
    case 'mist':   body = <path d="M4 8h13M6 12h12M5 16h11" />; break
    default:       body = <path d="M12 3c4 5 6 7.5 6 10a6 6 0 0 1-12 0c0-2.5 2-5 6-10Z" /> // drop
  }
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
      {body}
    </svg>
  )
}

// Title split into per-word reveal masks — words rise out of an overflow-hidden
// clip (crisp, Lusion-style; deliberately NO blur so copy is always readable).
// Words wrapped in *asterisks* render in the gold accent.
function Masked({ text }: { text: string }) {
  const segs: { w: string; a: boolean }[] = []
  text.split(/(\*[^*]+\*)/).forEach((part) => {
    const a = part.startsWith('*')
    part.replace(/\*/g, '').split(' ').filter(Boolean).forEach((w) => segs.push({ w, a }))
  })
  return (
    <>
      {segs.map((s, i) => (
        <span key={i} className="tpc-wmask">
          <span className={`tpc-ww${s.a ? ' tpc-accent' : ''}`}>{s.w}</span>
        </span>
      ))}
    </>
  )
}

const CSS = `
/* Palette lifted straight from the packaging: cream label paper, ink slab type,
   navy typewriter + wave band, on a warm espresso stage. No gold anywhere. */
.tpc-page { background:#0f0c0a; min-height:100vh; overflow-x:hidden; font-family:'Fira Code',monospace; position:relative;
  --accent:#e8dcc0; --glow:rgba(232,220,192,0.32); --paper:#f3f0e5; --ink:#17181c; --navy:#28355e; --navy-soft:#3d4c80; }
.tpc-vignette { position:fixed; inset:0; z-index:1; pointer-events:none; background:radial-gradient(circle at 50% 38%, transparent 30%, rgba(7,5,3,0.66) 100%); }
.tpc-grain { position:fixed; inset:-50%; z-index:1; pointer-events:none; opacity:0.03; mix-blend-mode:overlay;
  background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='180' height='180'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
  animation:tpcGrain 0.8s steps(4) infinite; }
@keyframes tpcGrain { 0%,100%{transform:translate(0,0)} 10%{transform:translate(-4%,-4%)} 30%{transform:translate(3%,-2%)} 50%{transform:translate(-2%,3%)} 70%{transform:translate(4%,2%)} 90%{transform:translate(-3%,1%)} }

/* Cinematic reading scrim — darkens the lower band so text is always legible
   over the glass, while the bottle stays bright above (product-in-shadow look). */
.tpc-scrim { position:fixed; left:0; right:0; bottom:0; height:66vh; z-index:2; pointer-events:none;
  background:linear-gradient(to top, rgba(11,8,6,0.95) 0%, rgba(11,8,6,0.86) 18%, rgba(11,8,6,0.52) 46%, rgba(11,8,6,0.2) 70%, transparent 100%); }

/* Step rail — one dot per section, clickable, right edge (Lusion-style index). */
.tpc-rail { position:fixed; right:22px; top:50%; transform:translateY(-50%); z-index:6; display:flex; flex-direction:column; gap:12px; opacity:1; transition:opacity 0.4s ease; }
.tpc-rail.hide { opacity:0; pointer-events:none; }
.tpc-rail button { width:22px; height:14px; display:flex; align-items:center; justify-content:flex-end; background:none; border:none; padding:0; cursor:pointer; }
.tpc-rail span { display:block; width:6px; height:6px; border-radius:50%; background:rgba(255,255,255,0.22); transition:all 0.35s ease; }
.tpc-rail button:hover span { background:rgba(255,255,255,0.55); }
.tpc-rail button.on span { width:20px; height:6px; border-radius:4px; background:var(--accent); box-shadow:0 0 10px var(--glow); }
.tpc-counter { position:fixed; left:26px; bottom:26px; z-index:6; font-size:0.72rem; letter-spacing:0.28em; color:rgba(255,255,255,0.4); transition:opacity 0.4s ease; font-variant-numeric:tabular-nums; }
.tpc-counter em { color:var(--accent); font-style:normal; }
.tpc-counter.hide { opacity:0; }

/* 100dvh (with 100vh fallback) — on mobile Chrome/Firefox 100vh includes the
   collapsed browser toolbar, so the bottom of each step hides behind the bar. */
.tpc-stage { height:100vh; height:100dvh; position:relative; overflow:hidden; z-index:3; }
.tpc-step { position:absolute; inset:0; display:flex; flex-direction:column; align-items:center; justify-content:center; text-align:center; padding:0 24px; pointer-events:none; opacity:0; visibility:hidden; }
.tpc-step[data-step="0"] { opacity:1; visibility:visible; }
.tpc-step.active { pointer-events:auto; }
.tpc-inner { display:flex; flex-direction:column; align-items:center; gap:24px; width:100%; will-change:transform,opacity; }
/* FIX #7 APPLIED — dvh fallback so the low caption isn't pushed under the mobile
   browser toolbar (11vh measures against the taller vh viewport and clips). */
.tpc-inner.low { position:absolute; left:0; right:0; bottom:11vh; bottom:11dvh; }
/* FIX #7 APPLIED — post-stage content sections keep their top clear of the fixed
   navbar when scrolled/anchored to, so nothing is hidden behind it on mobile. */
.tpc-collection, .tpc-compare, .tpc-notes-sec, .tpc-howsec, .tpc-faq, .tpc-ctaband { scroll-margin-top:calc(var(--tpc-nav-h, 72px) + 8px); }

/* Eyebrows sit over the 3D bottle, whose brightness swings wildly between steps
   (a lit cap behind an 11px letterform washed it out completely at some camera
   positions). Full-opacity ink plus its own darkening plate — not just a text
   shadow — so contrast no longer depends on what the camera is looking at.
   position/z-index keep it above the scrim and stop it being clipped. */
.tpc-eyebrow { position:relative; z-index:1; display:inline-block; font-family:'Fira Code',monospace; font-size:11px; font-weight:700;
  letter-spacing:0.42em; text-transform:uppercase; color:#f2ead6; margin:0;
  /* Soft darkening plate rather than a shadow alone. The old rgba(...,0.85)
     ink plus one shadow lost against the lit cap at some camera positions, and
     at 11px with 0.42em tracking the strokes are too thin to survive that.
     Kept borderless and blur-free so it reads as an extension of the scrim
     already behind this copy, not as a chip. */
  padding:5px 12px 5px 15px; border-radius:50px; background:rgba(10,7,5,0.5);
  text-shadow:0 1px 3px rgba(0,0,0,0.95), 0 2px 14px rgba(0,0,0,0.85); }
/* Poster-scale headline — the oryzo move: one huge line, nothing competing. */
.tpc-prompt { font-size:clamp(2.3rem,6.2vw,4.6rem); font-weight:300; line-height:1.08; color:#f3f1ea; font-family:'Fira Code',monospace; margin:0; letter-spacing:-0.015em; max-width:16ch; text-shadow:0 2px 26px rgba(0,0,0,0.9), 0 1px 3px rgba(0,0,0,0.75); }
/* FIX #6 APPLIED — italic accent words (e.g. "air") slant past their box on the
   right; the reveal mask's overflow:hidden then clips the final letter (the "r").
   A hair of right padding widens the glyph's box so the mask grows with it and
   the italic overhang stays inside the visible region. */
/* The 56px gold glow was clipped into a hard rectangle by the reveal mask's
   overflow:hidden — dropped it; keep only the subtle dark shadow for legibility. */
.tpc-accent { color:var(--accent); font-style:italic; padding-right:0.08em; text-shadow:0 2px 14px rgba(0,0,0,0.85); }
.tpc-sub { font-size:1.02rem; color:rgba(238,238,245,0.62); line-height:1.8; margin:0; max-width:42ch; text-shadow:0 1px 12px rgba(0,0,0,0.8); }

/* Word-mask reveal — words rise from below the clip line; no blur, ever. */
.tpc-wmask { display:inline-block; overflow:hidden; vertical-align:bottom; padding:0.04em 0 0.14em; margin:0 0.24em -0.14em 0; }
.tpc-wmask:last-child { margin-right:0; }
.tpc-ww { display:inline-block; will-change:transform; }

/* ── Pre-animation state (entry flash fix) ───────────────────────────────────
   The hero's reveal is driven by GSAP, which can only write its "from" state
   once its effect runs. Without help there were frames where the hero copy was
   painted in full, then snapped back to hidden and played in — the flash.
   These rules hold it pre-animation from the very first paint.

   The hold is keyed to .tpc-preload, which React puts on the root for exactly
   as long as the loader is up, and the reveal runs in a layout effect so GSAP's
   inline "from" values are written in the same commit that drops the class —
   before the browser paints either way.

   Keying it to a React-rendered class rather than one added imperatively is the
   important part: if the reveal never runs, the copy is simply visible. An
   imperative "now un-hide" class fails the other way round, leaving the hero
   blank until something else happens to write a transform over it. */
.tpc-page.tpc-preload:not(.is-static) .tpc-step[data-step="0"] .tpc-ww { transform:translateY(115%); }
.tpc-page.tpc-preload:not(.is-static) .tpc-step[data-step="0"] [data-fade],
.tpc-page.tpc-preload:not(.is-static) .tpc-step[data-step="0"] [data-item] { opacity:0; }

/* Brand wordmark — morphs from a horizontal lockup (--t:0) to a left-aligned
   3-line stack (--t:1). Per-word --hx / --ly are measured at runtime in JS. */
/* top clears the announcement strip as well as the navbar — the ticker is
   position:fixed, so it contributes no height and the offset has to be taken
   explicitly (--tpc-ticker-h is 0 when no ticker is mounted). The inline top
   GSAP writes during the corner morph is computed from the same base. */
.tpc-wordmark { position:fixed; top:calc(104px + var(--tpc-ticker-h, 0px)); left:50%; z-index:7; --t:0; pointer-events:none; will-change:transform,left,top; font-family:'Fira Code',monospace; }
.tpc-wm-word { position:absolute; display:inline-block; white-space:nowrap; margin:0;
  font-family:'Rockwell Condensed','Fira Code',monospace;
  font-size:clamp(1.2rem,2.7vw,1.95rem); font-weight:700; letter-spacing:0.3em; text-transform:uppercase;
  line-height:1.18; color:#ece2cd;
  text-shadow:0 2px 18px rgba(0,0,0,0.7), 0 0 30px rgba(232,220,192,0.2);
  -webkit-font-smoothing:antialiased;
  left:calc(var(--hx,0px) * (1 - var(--t))); top:calc(var(--ly,0px) * var(--t)); }
@media (max-width:600px) { .tpc-wordmark { top:calc(78px + var(--tpc-ticker-h, 0px)); } .tpc-wm-word { font-size:1.12rem; letter-spacing:0.22em; } }

.tpc-scrollcue { position:absolute; bottom:36px; left:50%; transform:translateX(-50%); display:flex; flex-direction:column; align-items:center; gap:10px; pointer-events:none; }
.tpc-scrollcue-text { font-size:10px; letter-spacing:0.3em; text-transform:uppercase; color:rgba(255,255,255,0.32); }
.tpc-scrollcue-line { width:1px; height:42px; background:linear-gradient(to bottom, rgba(232,220,192,0.6), transparent); }

.tpc-ctas { display:flex; gap:14px; flex-wrap:wrap; justify-content:center; }
.tpc-begin { padding:16px 42px; border-radius:50px; border:none; font-size:1rem; font-weight:700; cursor:pointer; color:var(--ink); background:linear-gradient(180deg,#f7f4ea,#e6dfcd); box-shadow:0 0 44px var(--glow), 0 10px 26px rgba(0,0,0,0.4); transition:transform 0.4s cubic-bezier(.2,.7,.2,1); }
.tpc-begin:hover { transform:translateY(-4px) scale(1.03); }
.tpc-explore { padding:16px 36px; border-radius:50px; border:1px solid rgba(255,255,255,0.2); background:rgba(255,255,255,0.03); color:rgba(255,255,255,0.75); font-size:1rem; cursor:pointer; backdrop-filter:blur(10px); transition:transform 0.4s cubic-bezier(.2,.7,.2,1), border-color 0.4s ease; }
.tpc-explore:hover { transform:translateY(-3px); border-color:rgba(232,220,192,0.5); }

/* Loader (cap, top view) → hero reveal — ring is centred to match the 3D cap */
.tpc-loader { position:fixed; inset:0; z-index:50; background:#0f0c0a; }
.tpc-loader-ring { position:absolute; top:50%; left:50%; transform:translate(-50%,-50%); }
/* Glow lives on a round pseudo-element (box-shadow follows border-radius), NOT
   as a drop-shadow filter on the SVG — a filter on the transformed circle gets
   composited as its rectangular bounds and shows up as a faint square panel. */
.tpc-loader-ring::before { content:''; position:absolute; inset:8%; border-radius:50%; box-shadow:0 0 36px rgba(232,220,192,0.28); }
.tpc-loader-ring svg { width:100%; height:100%; transform:rotate(-90deg); display:block; }
.tpc-ring-bg { fill:none; stroke:rgba(232,220,192,0.14); stroke-width:6; }
.tpc-ring-fg { fill:none; stroke:#e8dcc0; stroke-width:6; stroke-linecap:round; stroke-dasharray:339.3; stroke-dashoffset:339.3; }
.tpc-loader-meta { position:absolute; left:0; right:0; bottom:15%; display:flex; flex-direction:column; align-items:center; gap:12px; }
.tpc-loader-phase { font-family:'Fira Code',monospace; font-weight:300; font-size:1.05rem; color:rgba(232,232,240,0.82); letter-spacing:0.02em; margin:0; min-height:1.3em; }
.tpc-loader-brand { font-family:'Rockwell Condensed','Fira Code',monospace; font-weight:700; font-size:11px; letter-spacing:0.34em; text-transform:uppercase; color:rgba(232,220,192,0.6); margin:0; }

/* Reduced motion — everything stacks and scrolls natively. */
.tpc-page.is-static .tpc-wordmark { --t:1; left:24px; top:16px; transform:scale(0.62); transform-origin:left top; }
.tpc-page.is-static .tpc-stage { height:auto; overflow:visible; }
.tpc-page.is-static .tpc-step { position:relative; inset:auto; opacity:1; visibility:visible; min-height:100vh; min-height:100dvh; pointer-events:auto; }
.tpc-page.is-static .tpc-inner.low { position:relative; inset:auto; bottom:auto; }
.tpc-page.is-static .tpc-rail, .tpc-page.is-static .tpc-counter { display:none; }
.tpc-page.is-static .tpc-marquee-track { animation:none; }

@media (max-width:600px) {
  .tpc-rail { right:12px; gap:10px; }
  .tpc-counter { left:18px; bottom:18px; font-size:0.64rem; }
  .tpc-inner { gap:18px; }
  .tpc-inner.low { bottom:8vh; }
  .tpc-ctas { flex-direction:column; width:100%; max-width:320px; }
  .tpc-begin, .tpc-explore { width:100%; }
}

/* ── Post-experience: normal scroll resumes here ──
   z-index:10 + opaque bg lifts these above the fixed 3D canvas & overlays. */
.tpc-after { position:relative; z-index:10; background:#0f0c0a; }

/* Raised from 0.75 to full strength: at 11px with 0.42em tracking the letters
   are hairline-thin, and a 75%-opacity gold on near-black fell below a legible
   contrast ratio — the reason eyebrows read as "invisible" in some sections. */
.tpc-sec-eyebrow { text-align:center; font-family:'Fira Code',monospace; font-weight:700; font-size:11px; letter-spacing:0.42em; text-transform:uppercase; color:#e8dcc0; margin:0 0 18px; }
/* 52px under every section title was a screen-height of dead space on a laptop
   once the eyebrow's own margin was added on top; 30px still separates the
   heading from its content without leaving the band looking empty. */
.tpc-sec-title { text-align:center; font-family:'Fira Code',monospace; font-weight:300; font-size:clamp(2.4rem,5.4vw,4rem); color:#e8e8f0; margin:0 0 30px; letter-spacing:-0.015em; line-height:1.1; }
.tpc-sec-eyebrow.left, .tpc-sec-title.left { text-align:left; }

/* Light band — full-bleed packaging paper between the dark sections. */
.tpc-band-light { position:relative; background:var(--paper); }
.tpc-band-light .tpc-sec-eyebrow { color:var(--navy); }
.tpc-band-light .tpc-sec-title { color:var(--ink); }

/* Marquee — the endless brand line that opens the free-scroll editorial
   (Hungry Tiger move). Track holds two copies of the line; -50% loops clean. */
.tpc-marquee { overflow:hidden; padding:26px 0 24px; border-bottom:1px solid rgba(40,53,94,0.18); }
.tpc-marquee-track { display:flex; align-items:center; width:max-content; animation:tpcMarquee 32s linear infinite; }
.tpc-marquee-track span { font-family:'Fira Code',monospace; font-weight:300; font-style:italic; font-size:clamp(1.5rem,3.2vw,2.4rem); color:var(--ink); white-space:nowrap; letter-spacing:-0.01em; }
.tpc-marquee-track span::after { content:'✦'; margin:0 40px; font-size:0.55em; font-style:normal; color:var(--navy); vertical-align:middle; opacity:0.7; }
@keyframes tpcMarquee { to { transform:translateX(-50%); } }
@media (prefers-reduced-motion:reduce) { .tpc-marquee-track { animation:none; } }

/* Notes anatomy — three airy columns, hairline-ruled, no boxes. */
/* Section rhythm: no band pays more than 60px of padding on either edge on
   desktop (40px on a phone). Two adjacent sections still put ~120px between
   their content, which is the breathing room; anything more was reading as an
   empty page rather than as space. */
.tpc-notes-sec { max-width:1140px; margin:0 auto; padding:56px 24px 60px; }
.tpc-notes-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:36px 48px; }
.tpc-note-col { border-top:1px solid rgba(40,53,94,0.3); padding-top:24px; text-align:left; }
.tpc-note-tag { display:flex; align-items:center; gap:12px; font-family:'Fira Code',monospace; font-weight:700; font-size:0.72rem; letter-spacing:0.3em; text-transform:uppercase; color:var(--navy); }
.tpc-note-col h3 { font-family:'Fira Code',monospace; font-weight:400; font-size:clamp(1.5rem,2.6vw,2rem); color:var(--ink); margin:14px 0 10px; }
.tpc-note-col p { font-size:0.95rem; line-height:1.8; color:#4b4e59; margin:0; max-width:34ch; }

/* How it works — big editorial rows on the dark stage. The "01/02/03" gutter
   column is gone (section numbering removed), so the rows are full width. */
.tpc-howsec { max-width:1040px; margin:0 auto; padding:60px 24px 56px; }
.tpc-howsec .tpc-sec-title { margin-bottom:26px; }
.tpc-how-row { display:grid; grid-template-columns:1fr; gap:12px 40px; padding:24px 0; border-top:1px solid rgba(255,255,255,0.1); align-items:start; }
.tpc-how-row:last-of-type { border-bottom:1px solid rgba(255,255,255,0.1); }
.tpc-how-body h3 { font-family:'Fira Code',monospace; font-weight:300; font-size:clamp(1.7rem,3.6vw,2.6rem); color:#f0ede2; margin:0 0 10px; letter-spacing:-0.01em; }
.tpc-how-body p { font-size:0.98rem; line-height:1.8; color:rgba(238,238,245,0.6); margin:0; max-width:52ch; }

.tpc-imgslot { position:relative; aspect-ratio:3/4; border-radius:20px; overflow:hidden; background:#171310; border:1px solid rgba(255,255,255,0.08); }
/* Image is taller than its frame so the scrub parallax never shows gaps. */
.tpc-imgslot img { position:absolute; left:0; top:-8%; width:100%; height:116%; object-fit:cover; opacity:0; transition:opacity 0.6s ease; will-change:transform; }
.tpc-imgslot.has-img img { opacity:1; }
/* Branded placeholder — a CSS rendering of the real bottle so empty slots
   still read as art direction, never as a broken image. */
.tpc-ph { position:absolute; inset:0; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:34px; background:radial-gradient(circle at 50% 36%, #2c2117, #141009 75%); }
.tpc-ph-bottle { position:relative; width:92px; height:112px; border-radius:12px 12px 16px 16px; border:1px solid rgba(255,255,255,0.32); background:linear-gradient(165deg, rgba(255,255,255,0.16), rgba(255,255,255,0.02) 55%); }
.tpc-ph-bottle::before { content:''; position:absolute; left:50%; top:-36px; transform:translateX(-50%); width:64px; height:36px; border-radius:8px 8px 2px 2px; background:linear-gradient(160deg,#2e2e34,#0a0a0d); }
.tpc-ph-bottle span { position:absolute; left:13px; right:13px; top:24px; height:54px; border-radius:6px; background:var(--paper); }
.tpc-ph-bottle span::before { content:''; position:absolute; left:16%; right:16%; top:13px; height:3px; background:#17181c; box-shadow:0 9px 0 #17181c, 0 18px 0 rgba(40,53,94,0.75); }
.tpc-ph p { font-family:'Fira Code',monospace; font-weight:700; font-size:10px; letter-spacing:0.4em; text-transform:uppercase; color:rgba(232,220,192,0.5); margin:0; }

.tpc-collection { max-width:1140px; margin:0 auto; padding:56px 24px 60px; }
.tpc-col-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:24px; }
.tpc-col-card { position:relative; display:flex; flex-direction:column; justify-content:flex-end; height:400px; border-radius:22px; overflow:hidden; padding:26px 24px; text-decoration:none; border:1px solid rgba(255,255,255,0.08); background:rgba(255,255,255,0.03); transition:transform 0.45s cubic-bezier(.2,.7,.2,1), border-color 0.45s ease; }
.tpc-col-card:hover { transform:translateY(-6px); border-color:rgba(232,220,192,0.4); }
.tpc-col-card .tpc-imgslot { position:absolute; inset:0; aspect-ratio:auto; border-radius:0; border:none; background:transparent; transition:transform 0.6s cubic-bezier(.2,.7,.2,1); }
.tpc-col-card:hover .tpc-imgslot { transform:scale(1.04); }
.tpc-col-scrim { position:absolute; inset:0; background:linear-gradient(to top, rgba(8,6,4,0.94) 0%, rgba(8,6,4,0.6) 42%, rgba(8,6,4,0.1) 72%, transparent 100%); pointer-events:none; }

/* Multi-image card backdrop — see CardGallery. Stacked frames cross-fading in
   place, so it occupies exactly the same box as the single-image .tpc-imgslot
   and needs no layout of its own. */
.tpc-col-gal { position:absolute; inset:0; overflow:hidden; transition:transform 0.6s cubic-bezier(.2,.7,.2,1); }
.tpc-col-card:hover .tpc-col-gal { transform:scale(1.04); }
.tpc-col-gal-img { position:absolute; inset:0; width:100%; height:100%; object-fit:cover;
  opacity:0; transition:opacity 900ms ease; user-select:none; }
.tpc-col-gal-img.is-active { opacity:1; }
/* Progress pips sit top-right, clear of the copy and the buy row below. */
.tpc-col-gal-dots { position:absolute; top:14px; right:14px; display:flex; gap:5px; pointer-events:none; z-index:1; }
.tpc-col-gal-dots span { width:5px; height:5px; border-radius:50%; background:rgba(255,255,255,0.35);
  transition:width 0.3s ease, background 0.3s ease; }
.tpc-col-gal-dots span.is-active { width:14px; border-radius:3px; background:var(--gal-accent, #e8dcc0); }

@media (prefers-reduced-motion: reduce) {
  /* No cross-fade and no drift — the first frame, held. */
  .tpc-col-gal-img { transition:none; }
  .tpc-col-card:hover .tpc-col-gal { transform:none; }
}
/* Overlaid card copy sits over the image, so a placeholder's own brand label
   would clash — hide it (only shows when the real photo is missing). */
.tpc-col-card .tpc-ph p { display:none; }
.tpc-col-glow { position:absolute; inset:0; opacity:0.35; transition:opacity 0.45s ease; pointer-events:none; }
.tpc-col-card:hover .tpc-col-glow { opacity:0.55; }
.tpc-col-card h3 { position:relative; font-family:'Fira Code',monospace; font-weight:400; font-size:1.35rem; color:#f0ede2; margin:0 0 6px; }
/* Two-line clamp: catalogue descriptions vary from a phrase to a paragraph, and
   an unbounded one pushed the price and the button off the bottom of the card. */
.tpc-col-card p { position:relative; font-size:0.88rem; line-height:1.5; color:rgba(238,238,245,0.6); margin:0;
  display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; }

/* Buy row — price on the left, the card's own Add to Cart on the right. This
   replaced the "Shop the collection" link that used to be the section's only
   call to action, so the whole card is now actionable where it stands. */
.tpc-col-buy { position:relative; display:flex; align-items:center; justify-content:space-between; gap:12px; margin-top:14px; }
.tpc-col-price { font-family:'Fira Code',monospace; font-size:1rem; font-weight:700; color:var(--accent); }
.tpc-col-add { flex:none; padding:9px 18px; border-radius:50px; border:none; cursor:pointer;
  font-family:'Fira Code',monospace; font-size:0.78rem; font-weight:700; letter-spacing:0.04em;
  color:var(--ink); background:linear-gradient(180deg,#f7f4ea,#e6dfcd);
  transition:opacity 0.25s ease, transform 0.25s cubic-bezier(.2,.7,.2,1); }
.tpc-col-add:hover:not(:disabled) { transform:translateY(-2px); }
.tpc-col-add:disabled { cursor:default; }
.tpc-col-add.busy { opacity:0.6; }
.tpc-col-add.done { background:none; border:1px solid rgba(74,222,128,0.5); color:#4ade80; }
.tpc-col-add.out { background:rgba(255,255,255,0.06); border:1px solid rgba(255,255,255,0.1); color:#8a8a9a; }
/* FIX #3 (revised) — carousel dots for the mobile rails; hidden on desktop. */
.tpc-dots { display:none; }
.tpc-dot { appearance:none; -webkit-appearance:none; }

/* The trust strip that used to open this section was removed: it stated the
   same three shipping/payment facts as the line under the closing CTA. Kept the
   CTA one — it lands where the buying decision actually happens. */

/* The Difference — two hairline-ruled columns, ours quietly elevated. */
.tpc-compare { max-width:1140px; margin:0 auto; padding:56px 24px 60px; }
.tpc-cmp-grid { display:grid; grid-template-columns:1fr 1fr; gap:40px 48px; max-width:960px; margin:0 auto; }
.tpc-cmp-col { border-top:1px solid rgba(40,53,94,0.3); padding-top:24px; }
.tpc-cmp-tag { font-family:'Fira Code',monospace; font-weight:700; font-size:0.72rem; letter-spacing:0.3em; text-transform:uppercase; color:#8a8d99; }
.tpc-cmp-us .tpc-cmp-tag { color:var(--navy); }
.tpc-cmp-col ul { list-style:none; margin:20px 0 0; padding:0; display:flex; flex-direction:column; gap:14px; }
.tpc-cmp-col li { display:flex; gap:14px; font-size:0.98rem; line-height:1.7; color:#7a7d88; }
.tpc-cmp-us li { color:#2a2c36; font-family:'Fira Code',monospace; font-size:1.08rem; }
.tpc-cmp-col li em { font-style:normal; flex:none; color:#b0b2ba; padding-top:1px; }
.tpc-cmp-us li em { color:var(--navy); }

/* FAQ / practical details — hairline accordion rows on the dark stage, native
   details/summary. Rows animate in on first scroll-in; see PracticalDetails. */
.tpc-faq { max-width:840px; margin:0 auto; padding:56px 24px 60px; }
.tpc-faq .tpc-sec-title { margin-bottom:26px; }
.tpc-faq-row { border-top:1px solid rgba(255,255,255,0.1); }
.tpc-faq-row:last-of-type { border-bottom:1px solid rgba(255,255,255,0.1); }
.tpc-faq-row summary { display:flex; justify-content:space-between; align-items:baseline; gap:24px; cursor:pointer; list-style:none; padding:20px 4px; font-family:'Fira Code',monospace; font-weight:400; font-size:clamp(1.15rem,2.2vw,1.45rem); color:#e8e5da; transition:color 0.25s ease; }
.tpc-faq-row summary::-webkit-details-marker { display:none; }
.tpc-faq-row summary:hover { color:var(--accent); }
.tpc-faq-row summary span { flex:none; font-family:'Fira Code',monospace; font-weight:400; font-size:1.1rem; color:rgba(232,220,192,0.6); transition:transform 0.3s ease; }
.tpc-faq-row[open] summary span { transform:rotate(45deg); }
.tpc-faq-row p { margin:0; padding:0 4px 22px; font-size:0.96rem; line-height:1.8; color:rgba(238,238,245,0.6); max-width:62ch; }

/* CTA band trust line, under the button.
   Selector must stay more specific than the .tpc-ctaband p rule further down,
   which is both later in the sheet and more specific than a lone class — it was
   winning, so this line was getting margin:0 0 30px and sat flush against the
   button with a gap below it instead of above.
   The three facts are laid out rather than left to wrap: one row with separators
   where there's room, one fact per line where there isn't, so it never breaks
   mid-phrase with a stray separator at the end of a line. */
.tpc-ctaband .tpc-begin { display:inline-block; vertical-align:top; }
.tpc-ctaband .tpc-cta-line { line-height:0; }
.tpc-ctaband .tpc-cta-trust {
  position:relative; display:flex; flex-wrap:wrap; justify-content:center; align-items:center;
  font-family:'Fira Code',monospace; font-weight:700; font-size:0.68rem; letter-spacing:0.24em;
  text-transform:uppercase; line-height:1.5; color:rgba(243,240,229,0.62);
  margin:24px auto 0; max-width:760px;
}
.tpc-ctaband .tpc-cta-trust span + span::before { content:'·'; margin:0 14px; opacity:0.55; }
@media (max-width:720px) {
  /* Below this the row can't hold two facts without breaking one of them, so
     stack instead — reads as a deliberate list rather than bad wrapping. */
  .tpc-ctaband .tpc-cta-trust { flex-direction:column; gap:10px; margin-top:26px; letter-spacing:0.2em; }
  .tpc-ctaband .tpc-cta-trust span + span::before { display:none; }
}

/* CTA band — the navy canister band, full bleed, with the wave contour as a
   quiet watermark across the whole panel. */
.tpc-ctaband { position:relative; overflow:hidden; text-align:center; padding:60px 24px 56px; background:var(--navy); }
.tpc-ctaband::before { content:''; position:absolute; inset:0; opacity:0.08; pointer-events:none;
  background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='150' height='26'%3E%3Cpath d='M0 13 Q 18 3 37 13 T 75 13 T 112 13 T 150 13' stroke='%23f3f0e5' fill='none' stroke-width='1.4'/%3E%3C/svg%3E");
  background-repeat:repeat; background-size:150px 26px; }
.tpc-ctaband h2 { position:relative; font-family:'Fira Code',monospace; font-weight:300; font-size:clamp(2.6rem,6.4vw,4.8rem); color:#f3f0e5; margin:0 0 16px; line-height:1.08; letter-spacing:-0.015em; }
.tpc-ctaband p { position:relative; font-size:1rem; color:rgba(243,240,229,0.66); margin:0 0 24px; }
.tpc-ctaband .tpc-begin { position:relative; box-shadow:0 10px 26px rgba(0,0,0,0.35); }

.tpc-footer { border-top:1px solid rgba(255,255,255,0.08); padding:48px 24px 32px; }
.tpc-footer-grid { max-width:1140px; margin:0 auto; display:grid; grid-template-columns:1.6fr 1fr 1fr 1fr 1fr; gap:40px; }
.tpc-foot-name { font-family:'Rockwell Condensed','Fira Code',monospace; font-weight:700; font-size:1.7rem; color:#e8e8f0; letter-spacing:0.06em; margin:0 0 14px; }
.tpc-foot-sub { font-size:0.92rem; color:#8a8a9a; line-height:1.75; max-width:34ch; margin:0 0 24px; }
.tpc-foot-col h4 { font-size:11px; letter-spacing:0.24em; text-transform:uppercase; color:rgba(232,220,192,0.75); margin:0 0 20px; }
.tpc-foot-col a { display:block; font-size:0.92rem; color:rgba(255,255,255,0.6); text-decoration:none; margin-bottom:13px; transition:color 0.25s ease; width:fit-content; }
.tpc-foot-col a:hover { color:#e8dcc0; }
.tpc-foot-bottom { max-width:1140px; margin:32px auto 0; padding-top:22px; border-top:1px solid rgba(255,255,255,0.06); display:flex; justify-content:space-between; align-items:center; gap:18px; flex-wrap:wrap; }
.tpc-foot-copy { font-size:0.78rem; color:#6a6a7a; letter-spacing:0.04em; }
/* Social links are icons now, not words — sized as proper 40px tap targets. */
.tpc-foot-socials { display:flex; gap:10px; align-items:center; }
.tpc-foot-socials a { display:flex; align-items:center; justify-content:center; width:40px; height:40px; border-radius:50%;
  color:rgba(255,255,255,0.6); text-decoration:none; border:1px solid rgba(255,255,255,0.12);
  transition:color 0.25s ease, border-color 0.25s ease, transform 0.25s ease; }
.tpc-foot-socials a:hover { color:#e8dcc0; border-color:rgba(232,220,192,0.45); transform:translateY(-2px); }
.tpc-foot-socials svg { display:block; }

@media (max-width:860px) {
  /* FIX #3 APPLIED — on mobile the 3-up product/editorial grids overflow and
     clip, so switch them to a snap-scrolling horizontal rail with a hidden
     scrollbar; desktop keeps the grid. */
  .tpc-col-grid {
    display:flex; flex-direction:row; overflow-x:auto;
    scroll-snap-type:x mandatory; -webkit-overflow-scrolling:touch;
    gap:16px; padding-bottom:16px; max-width:none; margin:0;
    -ms-overflow-style:none; scrollbar-width:none;
  }
  .tpc-col-grid::-webkit-scrollbar { display:none; }
  /* Height tracks the viewport so the ENTIRE card (image + copy + CTA) plus the
     dots below always fit within one mobile screen. */
  /* The card now carries a price and an Add to Cart button under the copy, so
     it needs more room than the old 270×330 — but the extra height went in
     without the width following, which squeezed the photo into a much narrower
     crop than the desktop card and left the copy cramped against the button.
     Width is taken from the viewport so the card keeps roughly the same
     proportions on every phone, and the type inside steps down with it. */
  .tpc-col-card {
    flex:0 0 auto; width:min(74vw, 300px); scroll-snap-align:start;
    height:min(384px, calc(100dvh - 300px)); min-height:300px;
    padding:20px 18px;
  }
  .tpc-col-card h3 { font-size:1.15rem; }
  .tpc-col-card p { font-size:0.82rem; }
  .tpc-col-buy { margin-top:12px; }
  .tpc-col-price { font-size:0.92rem; }
  .tpc-col-add { padding:8px 15px; font-size:0.72rem; }

  .tpc-notes-grid, .tpc-cmp-grid { grid-template-columns:1fr; max-width:460px; margin:0 auto; gap:32px; }
  .tpc-dots { display:flex; justify-content:center; align-items:center; gap:9px; margin:18px 0 0; }
  .tpc-dot { width:7px; height:7px; padding:0; border:none; border-radius:50%; cursor:pointer;
    background:rgba(232,220,192,0.3); transition:width 0.3s ease, background 0.3s ease, box-shadow 0.3s ease; }
  .tpc-dot.on { width:22px; border-radius:4px; background:var(--accent); box-shadow:0 0 10px var(--glow); }
  .tpc-how-row { gap:6px; padding:20px 0; }
  .tpc-footer-grid { grid-template-columns:1fr 1fr; gap:24px; }
  .tpc-foot-brand { grid-column:1 / -1; }
}
@media (max-width:600px) {
  /* The 84px gap under section titles pushed the rail + dots off-screen on
     phones; trim it (and the oversized headline) so the carousel + dots fit in
     one view, clear of the fixed navbar. */
  .tpc-sec-title { font-size:clamp(1.9rem,7.4vw,2.6rem); margin-bottom:20px; }
  /* Phones cap at 40px per edge — see the section-rhythm note above. */
  .tpc-notes-sec { padding:40px 22px 40px; }
  .tpc-howsec { padding:40px 22px 40px; }
  .tpc-collection { padding:36px 22px 40px; }
  .tpc-compare { padding:40px 22px 40px; }
  .tpc-faq { padding:40px 22px 40px; }
  .tpc-ctaband { padding:40px 22px 40px; }
  .tpc-footer { padding:36px 22px 28px; }
  .tpc-foot-bottom { flex-direction:column; align-items:flex-start; }
}
`


/**
 * Mounts the Collection band the first time the visitor gets near it.
 *
 * The 700px rootMargin is the whole point: the chunk downloads, the catalogue
 * request goes out and the images start decoding while the band is still a
 * screen away, so by the time it is actually on screen it is already populated
 * and there is no pop-in. Once triggered the observer disconnects — this is a
 * one-way door, the section never unmounts.
 *
 * There is deliberately no Suspense fallback: the band hides itself when no
 * product is flagged for the landing page, so a placeholder would reserve space
 * for something that may never arrive. Nothing → then the section, or nothing.
 */
function DeferredCollection({ onReady }: { onReady?: () => void }) {
  const ref = useRef<HTMLDivElement>(null)
  const [mount, setMount] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    // No IntersectionObserver (very old browser) → just render it.
    if (typeof IntersectionObserver === 'undefined') { setMount(true); return }

    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setMount(true)
        io.disconnect()
      }
    }, { rootMargin: '700px 0px' })
    io.observe(el)
    return () => io.disconnect()
  }, [])

  return (
    <div ref={ref}>
      {mount && (
        <Suspense fallback={null}>
          <CollectionSection onReady={onReady} />
        </Suspense>
      )}
    </div>
  )
}

/**
 * The practical details (FAQ) rows.
 *
 * Entrance is driven by a plain IntersectionObserver rather than the page's
 * GSAP reveal, so each row can carry its own 100ms step: they fade up one after
 * another the first time the section is scrolled into view, and the observer is
 * disconnected after that single trigger. Reduced-motion visitors get the
 * finished state on the first paint and no observer at all.
 */
function PracticalDetails() {
  const secRef = useRef<HTMLElement>(null)
  const [shown, setShown] = useState(() => prefersReducedMotion())

  useEffect(() => {
    if (shown) return
    const el = secRef.current
    if (!el) return
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setShown(true)
        io.disconnect()
      }
    }, { threshold: 0.15 })
    io.observe(el)
    return () => io.disconnect()
  }, [shown])

  return (
    <section className="tpc-faq" ref={secRef}>
      <p className="tpc-sec-eyebrow" data-reveal>Before You Begin</p>
      <h2 className="tpc-sec-title" data-reveal>The practical details.</h2>
      <div>
        {FAQ.map((f, i) => (
          <details
            key={f.q}
            className="tpc-faq-row"
            style={{
              opacity: shown ? 1 : 0,
              transform: shown ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 400ms ease-out, transform 400ms ease-out',
              transitionDelay: `${i * 100}ms`,
            }}
          >
            <summary>{f.q}<span aria-hidden="true">+</span></summary>
            <p>{f.a}</p>
          </details>
        ))}
      </div>
    </section>
  )
}

export function Landing() {
  const navigate = useNavigate()
  const rootRef = useRef<HTMLDivElement>(null)
  const stageRef = useRef<HTMLDivElement>(null)
  const loaderRef = useRef<HTMLDivElement>(null)
  const ringRef = useRef<SVGCircleElement>(null)
  const phaseRef = useRef<HTMLSpanElement>(null)
  const [step, setStep] = useState(0)
  const [free, setFree] = useState(false) // finale passed → native scroll below
  const [loaded, setLoaded] = useState(false)

  // Navigation internals — refs so Observer/keyboard handlers never go stale.
  const stepRef = useRef(0)
  const animRef = useRef(false)
  const freeRef = useRef(false)
  const freedAtRef = useRef(0)
  const observerRef = useRef<Observer | null>(null)
  const goToRef = useRef<(i: number) => void>(() => {})
  // Scroll milestones already reported for this mount — see the depth effect.
  const depthFiredRef = useRef<Set<number>>(new Set())
  // Step requested while a transition was still running, as a target index.
  // Without this a gesture arriving mid-transition was thrown away outright, so
  // scrolling twice in quick succession moved you one step and the second
  // scroll felt dead — the "not smooth" complaint. The queued target is played
  // the moment the current transition lands, and queueing also speeds the
  // running transition up (see tlRef), so repeated input flows instead of
  // stalling. Held as an index rather than a direction so the rail dots, which
  // jump several steps at once, queue correctly too.
  const pendingRef = useRef<number | null>(null)
  const tlRef = useRef<gsap.core.Timeline | null>(null)

  // The bottle reads filled with perfume from the first frame — the liquid is
  // the show now (it sloshes on every scroll) — and quietly tops off by the finale.
  const fill = 0.55 + 0.45 * (step / (STEPS.length - 1))

  // ── Step navigation (Observer-driven; one gesture = exactly one step) ──
  function stepEls(): HTMLElement[] {
    return Array.from(stageRef.current?.querySelectorAll<HTMLElement>('[data-step]') ?? [])
  }

  function setWordmark(corner: boolean) {
    const wm = rootRef.current?.querySelector('[data-brandmark]') as HTMLElement | null
    if (!wm) return
    // GSAP writes an inline `top`, which beats the stylesheet's calc() — so the
    // announcement strip's height has to be added here too, or the wordmark
    // snaps back under the ticker the first time it morphs out of the corner.
    // Taken from the constant rather than the live --tpc-ticker-h: this runs
    // from the step handler, BEFORE the ticker's effect has updated the property,
    // and the centred wordmark only ever exists on step 0 — where the ticker is
    // always shown.
    const mobile = window.innerWidth < 600
    const baseTop = (mobile ? 78 : 104) + (mobile ? TICKER_HEIGHT_MOBILE : TICKER_HEIGHT)
    gsap.to(wm, { left: corner ? 24 : '50%', top: corner ? 14 : baseTop, scale: corner ? 0.62 : 1, transformOrigin: 'left top', duration: 0.9, ease: 'power2.inOut' })
    const from = parseFloat(wm.style.getPropertyValue('--t') || '0')
    const prox = { t: from }
    gsap.to(prox, { t: corner ? 1 : 0, duration: 0.9, ease: 'power2.inOut', onUpdate: () => wm.style.setProperty('--t', prox.t.toFixed(3)) })
  }

  function release() {
    // Finale passed — hand control back to the browser for the sections below.
    // Drop any queued step: it belongs to the locked journey we're leaving.
    pendingRef.current = null
    freeRef.current = true
    freedAtRef.current = Date.now()
    setFree(true)
    observerRef.current?.disable()
    document.body.style.overflow = ''
    requestAnimationFrame(() => {
      ScrollTrigger.refresh()
      window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })
    })
  }

  function relock() {
    if (!freeRef.current) return
    pendingRef.current = null
    freeRef.current = false
    setFree(false)
    window.scrollTo(0, 0)
    document.body.style.overflow = 'hidden'
    observerRef.current?.enable()
  }

  function goTo(i: number) {
    const cur = stepRef.current
    if (freeRef.current || i === cur || i < 0) return

    // Mid-transition, remember the request instead of discarding it and speed
    // the running transition up so the queued step begins almost immediately.
    // Every entry point (wheel, touch, keyboard, rail dots) benefits.
    //
    // 3x is chosen against the input cadence, not for looks: a continuous
    // scroll asks for a step roughly every 360ms, and a full-speed transition
    // takes ~1.4s. Left at 1x the queue would always be a step behind and the
    // page would feel like it was lagging the user. At 3x a chained step lands
    // in ~0.5s, so the beats keep pace. A single unqueued step still plays at
    // full cinematic speed — this only bites when someone is scrolling on.
    if (animRef.current) {
      pendingRef.current = i
      tlRef.current?.timeScale(3)
      return
    }

    if (i >= STEPS.length) { release(); return }

    const els = stepEls()
    const from = els[cur], to = els[i]
    if (!from || !to) return
    const dir = i > cur ? 1 : -1
    animRef.current = true
    stepRef.current = i
    setStep(i) // camera starts flying to the new view immediately
    if ((cur === 0) !== (i === 0)) setWordmark(i !== 0)

    const words = to.querySelectorAll('.tpc-ww')
    const fades = to.querySelectorAll('[data-fade]')
    const items = to.querySelectorAll('[data-item]')

    // Short cooldown after the transition swallows the tail of trackpad
    // momentum, so one flick can never double-advance. Anything the user
    // deliberately queued while this ran is played out immediately after,
    // which is what keeps repeated scrolling feeling continuous.
    const tl = gsap.timeline({
      onComplete: () => {
        gsap.delayedCall(0.05, () => {
          animRef.current = false
          tlRef.current = null
          const queued = pendingRef.current
          pendingRef.current = null
          if (queued !== null) goToRef.current(queued)
        })
      },
    })
    tlRef.current = tl
    // Exit — quick, crisp, directional. Deliberately no blur: the outgoing copy
    // stays readable to the last frame.
    const fromAnims = from.querySelectorAll('[data-anim]')
    // The stagger extends the exit past 0.4s; clearProps must land AFTER the
    // last element's tween ends, or the tween re-writes opacity:0 on top of the
    // cleared styles and the step comes back permanently invisible.
    const exitEnd = 0.4 + 0.035 * Math.max(0, fromAnims.length - 1)
    tl.to(fromAnims, { y: -46 * dir, opacity: 0, duration: 0.4, ease: 'power2.in', stagger: 0.035 }, 0)
    tl.set(from, { autoAlpha: 0 }, 0.42)
    tl.set(fromAnims, { clearProps: 'transform,opacity' }, exitEnd + 0.03)
    tl.set(from.querySelectorAll('.tpc-ww'), { clearProps: 'all' }, exitEnd + 0.03)
    // Enter — words rise out of their masks, then supporting copy and cards.
    if (words.length) tl.set(words, { yPercent: 115 * dir }, 0.4)
    if (fades.length) tl.set(fades, { opacity: 0, y: 18 * dir }, 0.4)
    if (items.length) tl.set(items, { opacity: 0, y: 34 * dir, scale: 0.96 }, 0.4)
    tl.set(to, { autoAlpha: 1 }, 0.42)
    if (words.length) tl.to(words, { yPercent: 0, duration: 0.85, ease: 'power4.out', stagger: 0.05 }, 0.46)
    if (fades.length) tl.to(fades, { opacity: 1, y: 0, duration: 0.6, ease: 'power3.out', stagger: 0.08 }, 0.55)
    if (items.length) tl.to(items, { opacity: 1, y: 0, scale: 1, duration: 0.55, ease: 'power3.out', stagger: 0.05 }, 0.6)
  }
  goToRef.current = goTo

  // `from` names WHICH of the page's three identical asks was pressed — the
  // opening hero, the finale that closes the locked journey, or the band above
  // the footer. They all go to the same place, so without it a click is just a
  // click and there is no way to tell "nobody scrolls that far" apart from
  // "everybody scrolls that far and the copy down there doesn't land".
  function craft(from: string) {
    trackCtaClick(from)
    // No sign-up wall in front of the quiz any more: a guest crafts their
    // formula, adds it to a local bag, and is only asked to sign in at payment.
    navigate('/craft')
  }

  // Loader → hero reveal (runs once)
  useEffect(() => {
    if (prefersReducedMotion()) { setLoaded(true); return }
    document.body.style.overflow = 'hidden'
    const CIRC = 339.3
    const PHASES = ['Gathering essences', 'Warming the oils', 'Composing accords', 'Balancing the blend', 'Sealing the bottle']
    const o = { v: 0 }
    const tl = gsap.timeline()
    // 0–1.7s: the gold cap-ring fills as oils "load" (camera holds top-down)
    tl.to(o, {
      v: 100, duration: 1.7, ease: 'power1.inOut',
      onUpdate: () => {
        if (ringRef.current) ringRef.current.style.strokeDashoffset = String(CIRC * (1 - o.v / 100))
        if (phaseRef.current) {
          const idx = Math.min(PHASES.length - 1, Math.floor(o.v / (100 / PHASES.length)))
          if (phaseRef.current.textContent !== PHASES[idx]) phaseRef.current.textContent = PHASES[idx]
        }
      },
    })
    // 1.7s: ring dissolves to reveal the real cap held top-down (the "becomes the cap" beat)
    tl.to(loaderRef.current, { opacity: 0, duration: 0.5, ease: 'power2.inOut', onComplete: () => setLoaded(true) }, 1.7)
    return () => { tl.kill(); document.body.style.overflow = '' }
  }, [])

  // Hero text reveal — word masks rise when the loader clears (no blur).
  //
  // A layout effect, not a plain one: it runs in the same commit that drops
  // .tpc-preload from the root and before the browser paints, so GSAP's inline
  // "from" values are already on the elements the first time the hero is
  // painted without the CSS hold. That's what removes the flash — no rAF and no
  // imperative class needed, and if this effect never ran the hero would just
  // be visible rather than stuck blank.
  useLayoutEffect(() => {
    if (!loaded || prefersReducedMotion()) return

    const hero = stepEls()[0]
    if (!hero) return

    const ctx = gsap.context(() => {
      gsap.fromTo(hero.querySelectorAll('.tpc-ww'),
        { yPercent: 115 },
        { yPercent: 0, duration: 0.95, stagger: 0.055, ease: 'power4.out', delay: 1.2 })
      gsap.fromTo(hero.querySelectorAll('[data-fade]'),
        { opacity: 0, y: 18 },
        { opacity: 1, y: 0, duration: 0.7, stagger: 0.1, ease: 'power3.out', delay: 1.7 })
      gsap.fromTo(hero.querySelectorAll('[data-item]'),
        { opacity: 0, y: 24, scale: 0.96 },
        { opacity: 1, y: 0, scale: 1, duration: 0.6, stagger: 0.08, ease: 'power3.out', delay: 2.0 })
    }, rootRef)

    return () => ctx.revert()
  }, [loaded])

  // Scroll hijack — Observer turns wheel/touch gestures into discrete steps.
  // The page hard-locks on every section: nothing can be skipped, nothing
  // half-enters. Enabled once the intro camera sweep lands on the hero.
  useEffect(() => {
    if (!loaded || prefersReducedMotion()) return
    document.body.style.overflow = 'hidden'

    // Momentum guard: a trackpad flick keeps emitting wheel events for seconds,
    // so a step disarms the observer and it re-arms on one of two conditions.
    //
    //   QUIET  — ~120ms with no movement at all. This is the flick case: the
    //            tail has died, the user is done, the next gesture is new
    //            intent and should land immediately.
    //
    //   CADENCE— ~360ms since the last accepted step while movement continues.
    //            This is the one that was missing. Quiet alone cannot re-arm
    //            during continuous scrolling, because every event reset the
    //            quiet timer before it could fire — so the journey froze on one
    //            step until the user stopped scrolling entirely, which is the
    //            "sticking" report. With a cadence, holding a scroll walks the
    //            beats at a steady pace instead of deadlocking.
    //
    // The trade is that a single hard flick with a long tail can now carry more
    // than one beat. That is the correct trade here: every step still renders
    // in order and nothing is skipped, and a page that keeps responding beats
    // one that stops dead.
    const QUIET_MS   = 120
    const CADENCE_MS = 360
    let armed = true
    let lastStepAt = 0
    let quietT: ReturnType<typeof setTimeout> | undefined

    // stepRef already points at the in-flight destination, so during a
    // transition this reads as "one beyond wherever we're heading" — which is
    // what a second scroll means. goTo queues it; nothing is dropped.
    const tryStep = (d: number) => {
      if (!armed || freeRef.current) return
      armed = false
      lastStepAt = Date.now()
      goToRef.current(stepRef.current + d)
    }
    const obs = Observer.create({
      target: window,
      type: 'wheel,touch',
      wheelSpeed: -1,
      tolerance: 10,
      preventDefault: true,
      ignore: 'button,a,input,textarea,select',
      onChangeY: () => {
        // Sustained input path: re-arm on a cadence so a continuous scroll
        // keeps advancing. Checked here rather than on a timer because this
        // fires on every raw movement, so it can only be reached while the
        // user is still actually scrolling.
        if (!armed && Date.now() - lastStepAt >= CADENCE_MS) armed = true

        clearTimeout(quietT)
        quietT = setTimeout(() => { armed = true }, QUIET_MS)
      },
      onDown: () => tryStep(-1),
      onUp: () => tryStep(1),
    })
    obs.disable()
    observerRef.current = obs
    // Was 1500ms. A visitor who starts scrolling as soon as the loader clears
    // hit a dead page for a second and a half and read it as broken — the
    // journey only needs long enough for the hero's first words to land.
    const enableT = setTimeout(() => { if (!freeRef.current) obs.enable() }, 900)

    const onKey = (e: KeyboardEvent) => {
      if (freeRef.current) return
      if (['ArrowDown', 'PageDown', ' '].includes(e.key)) { e.preventDefault(); goToRef.current(stepRef.current + 1) }
      else if (['ArrowUp', 'PageUp'].includes(e.key)) { e.preventDefault(); goToRef.current(stepRef.current - 1) }
    }
    // Re-entry from the free-scroll zone: reaching the very top hands control
    // back to the journey (desktop wheel-up, or any touch scroll landing on 0).
    const onWheel = (e: WheelEvent) => {
      if (freeRef.current && e.deltaY < -4 && window.scrollY <= 2) relock()
    }
    const onScroll = () => {
      if (freeRef.current && window.scrollY === 0 && Date.now() - freedAtRef.current > 1200) relock()
    }
    window.addEventListener('keydown', onKey)
    window.addEventListener('wheel', onWheel, { passive: true })
    window.addEventListener('scroll', onScroll, { passive: true })

    return () => {
      clearTimeout(enableT)
      clearTimeout(quietT)
      obs.kill()
      observerRef.current = null
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('wheel', onWheel)
      window.removeEventListener('scroll', onScroll)
      document.body.style.overflow = ''
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded])

  // Static fallback for reduced motion + always start the journey at the top
  // (the browser must not restore a mid-page scroll position into the lock).
  useEffect(() => {
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual'
    window.scrollTo(0, 0)
    if (prefersReducedMotion()) { rootRef.current?.classList.add('is-static'); return }

    // Scroll pin — mobile browsers (iOS especially) let hard touch flicks
    // scroll natively PAST body overflow:hidden, and the Observer ignores
    // touches that start on buttons/links. Any leaked offset while the journey
    // is locked is snapped straight back to 0, so the editorial sections can
    // never ride up over the bottle. overscroll-behavior kills the elastic
    // bounce that starts those leaks.
    document.documentElement.style.overscrollBehavior = 'none'
    const pin = () => { if (!freeRef.current && window.scrollY !== 0) window.scrollTo(0, 0) }
    window.addEventListener('scroll', pin, { passive: true })
    return () => {
      window.removeEventListener('scroll', pin)
      document.documentElement.style.overscrollBehavior = ''
    }
  }, [])

  // ── Scroll depth ───────────────────────────────────────────────────────────
  // How far down this page a visitor actually reads. Landing only — it is the
  // one route long enough for the number to mean anything, and also the
  // heaviest, so the listener is built to cost nothing:
  //
  //  • passive, so the browser never waits on it to decide whether to scroll;
  //  • coalesced into one requestAnimationFrame, so a flick that fires eighty
  //    scroll events does at most one measurement per frame — reading
  //    scrollHeight forces layout, and doing that per tick is exactly the kind
  //    of thing that turns a smooth scroll into a janky one;
  //  • a Set of the thresholds already sent, so each fires once per page view.
  //
  // Note the locked journey pins scrollY to 0 (the pin above), so nothing can
  // trip until release() hands scrolling back for the editorial sections. That
  // is correct rather than a gap: the journey has its own measure in `step`,
  // and these milestones describe the long page underneath it.
  useEffect(() => {
    const fired = depthFiredRef.current
    let frame = 0

    const measure = () => {
      frame = 0
      const doc = document.documentElement
      // A viewport taller than the document can't be scrolled — and would
      // otherwise report 100% to everyone who merely arrived.
      if (doc.scrollHeight - window.innerHeight <= 0) return
      const seen = ((window.scrollY + window.innerHeight) / doc.scrollHeight) * 100
      for (const t of SCROLL_THRESHOLDS) {
        if (seen >= t && !fired.has(t)) {
          fired.add(t)
          trackScrollDepth(t)
        }
      }
    }

    const onScroll = () => {
      if (frame) return
      frame = requestAnimationFrame(measure)
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      if (frame) cancelAnimationFrame(frame)
      // A remount is a new page view, so the milestones start over.
      fired.clear()
    }
  }, [])

  // Measure the wordmark so it can morph between one horizontal line and a
  // left-aligned 3-line stack. --hx = each word's x in the horizontal lockup
  // (centred around the container origin); --ly = its y in the stack.
  useEffect(() => {
    const el = rootRef.current?.querySelector('[data-brandmark]') as HTMLElement | null
    if (!el) return
    const words = Array.from(el.querySelectorAll<HTMLElement>('.tpc-wm-word'))
    if (!words.length) return
    const GAP = 6 // px between words on the horizontal line (letter-spacing adds the rest)
    const layout = () => {
      const w = words.map((x) => x.offsetWidth)        // offsetWidth ignores scale
      const lineH = words[0].offsetHeight || 28
      const total = w.reduce((a, b) => a + b, 0) + GAP * (words.length - 1)
      let cum = 0
      words.forEach((word, i) => {
        word.style.setProperty('--hx', (-total / 2 + cum).toFixed(1) + 'px')
        word.style.setProperty('--ly', (i * lineH).toFixed(1) + 'px')
        cum += w[i] + GAP
      })
    }
    layout()
    const fonts: any = (document as any).fonts
    if (fonts?.ready) fonts.ready.then(layout)
    window.addEventListener('resize', layout)
    return () => window.removeEventListener('resize', layout)
  }, [])

  // Reveal-on-scroll for the post-journey sections (craft, collection,
  // testimonials, footer). These live in normal flow below the stage, so plain
  // ScrollTriggers work once scroll is released.
  useEffect(() => {
    if (prefersReducedMotion()) return
    const ctx = gsap.context(() => {
      gsap.utils.toArray<HTMLElement>('[data-reveal]').forEach((el) => {
        gsap.fromTo(el,
          { opacity: 0, y: 42 },
          { opacity: 1, y: 0, duration: 0.9, ease: 'power3.out',
            scrollTrigger: { trigger: el, start: 'top 88%' } })
      })
      // Soft parallax on the editorial photos — the image (sized 116% of its
      // frame) drifts as the frame crosses the viewport.
      gsap.utils.toArray<HTMLElement>('.tpc-imgslot img').forEach((img) => {
        gsap.fromTo(img, { yPercent: -5 }, { yPercent: 5, ease: 'none',
          scrollTrigger: { trigger: img.parentElement, start: 'top bottom', end: 'bottom top', scrub: true } })
      })
    }, rootRef)
    return () => ctx.revert()
  }, [])

  // ── Step renderers ──
  function renderStep(sc: Step, i: number) {
    const active = step === i
    const cls = `tpc-step ${active ? 'active' : ''}`
    if (sc.k === 'hero') {
      return (
        <section key={i} data-step={i} className={cls}>
          <div data-inner className="tpc-inner low">
            {/* No eyebrow on the opening scene — the headline lands harder with
                nothing above it. The product facts are stated in the FAQ and at
                checkout, where they're actually needed. */}
            <h1 data-anim className="tpc-prompt" style={{ maxWidth: '15ch' }}><Masked text="Designed to be *remembered.*" /></h1>
            <p data-anim data-fade className="tpc-sub">We translate who you are into a fragrance that has never existed.</p>
            <div data-anim className="tpc-ctas">
              <button data-item className="tpc-begin" onClick={() => craft('hero')}>Craft your scent →</button>
            </div>
          </div>
          <div data-anim data-fade className="tpc-scrollcue"><span className="tpc-scrollcue-text">Scroll for the story</span><div className="tpc-scrollcue-line" /></div>
        </section>
      )
    }
    if (sc.k === 'portal') {
      // Nose to the glass: the camera holds the close "portal" framing while the
      // liquid inside — kicked by every scroll — takes the stage. Just a caption.
      return (
        <section key={i} data-step={i} className={cls}>
          <div data-inner className="tpc-inner low">
            <p data-anim data-fade className="tpc-eyebrow">In the Glass</p>
            <p data-anim data-fade className="tpc-sub">Fifty millilitres of a formula that exists for one person on earth.</p>
          </div>
        </section>
      )
    }
    if (sc.k === 'text') {
      return (
        <section key={i} data-step={i} className={cls}>
          <div data-inner className="tpc-inner low">
            <p data-anim data-fade className="tpc-eyebrow">{sc.eyebrow}</p>
            <h2 data-anim className="tpc-prompt"><Masked text={sc.title} /></h2>
            {sc.sub && <p data-anim data-fade className="tpc-sub">{sc.sub}</p>}
          </div>
        </section>
      )
    }
    // finale
    return (
      <section key={i} data-step={i} className={cls}>
        <div data-inner className="tpc-inner low">
          <p data-anim data-fade className="tpc-eyebrow">Your Turn</p>
          <h2 data-anim className="tpc-prompt" style={{ maxWidth: '18ch' }}>
            <Masked text="Your scent reflection *is here.*" />
          </h2>
          <p data-anim data-fade className="tpc-sub">Ten questions, two minutes. Two compositions come back — you choose the one that’s you.</p>
          <div data-anim className="tpc-ctas">
            <button data-item className="tpc-begin" onClick={() => craft('finale')}>Craft your scent →</button>
            <button data-item className="tpc-explore" onClick={() => navigate('/shop')}>Explore the collection</button>
          </div>
        </div>
      </section>
    )
  }

  return (
    // tpc-preload holds the hero copy pre-animation for exactly as long as the
    // loader is up — see the pre-animation block in the stylesheet.
    <div ref={rootRef} className={`tpc-page${loaded ? '' : ' tpc-preload'}`}>
      <style>{CSS}</style>

      {!loaded && (
        <div ref={loaderRef} className="tpc-loader">
          <div className="tpc-loader-ring" style={{ width: capRingPx(), height: capRingPx() }}>
            <svg viewBox="0 0 132 132">
              <circle className="tpc-ring-bg" cx="66" cy="66" r="54" />
              <circle ref={ringRef} className="tpc-ring-fg" cx="66" cy="66" r="54" />
            </svg>
          </div>
          <div className="tpc-loader-meta">
            <p className="tpc-loader-phase"><span ref={phaseRef}>Gathering essences</span></p>
            <p className="tpc-loader-brand">The Perfume Culture</p>
          </div>
        </div>
      )}

      {/* Announcement strip — fixed directly under the navbar, and only for the
          opening beats.
          • !loaded — it outranks the intro loader (z-index 999 vs 50), so without
            this it paints a gold bar across the loading screen; it slides in with
            the rest of the page instead.
          • step > 1 — the locked journey moves between steps WITHOUT scrolling the
            window, so the ticker's own scroll check never fires here; the step
            index is what tells it the visitor has moved on. Past the second step
            it retracts for good rather than sitting over the editorial bands. */}
      <OfferTicker hidden={!loaded || step > 1 || free} />

      {/* Liquid tinted to the real juice — rich honey-amber (from the photos).
          The fallback stands in for the scene's own fixed, full-viewport canvas
          (same inset/z-index/backdrop), so the chunk arriving causes no shift
          and no flash — the stage is simply empty espresso until it does. */}
      <Suspense fallback={
        <div style={{ position: 'fixed', inset: 0, zIndex: 0, background: '#0f0c0a', pointerEvents: 'none' }} />
      }>
        <CinematicScene fill={fill} color="#d08a20" view={STEPS[step].view} />
      </Suspense>

      {/* Brand wordmark — horizontal lockup over the hero that stacks and travels
          to the top-left corner when the journey begins. Hidden under the loader
          during intro (loader z-index 50 > 7). Word offsets measured in an effect. */}
      <div data-brandmark className="tpc-wordmark" aria-label="The Perfume Culture">
        <span className="tpc-wm-word">The</span>
        <span className="tpc-wm-word">Perfume</span>
        <span className="tpc-wm-word">Culture</span>
      </div>

      <div className="tpc-vignette" />
      <div className="tpc-scrim" />
      <div className="tpc-grain" />

      {/* Step rail + counter — always know where you are in the story. */}
      <div className={`tpc-rail ${free || !loaded ? 'hide' : ''}`} aria-label="Sections">
        {STEPS.map((s, i) => (
          <button key={i} className={step === i ? 'on' : ''} aria-label={`Section ${i + 1}`} onClick={() => goToRef.current(i)}>
            <span />
          </button>
        ))}
      </div>
      <div className={`tpc-counter ${free || !loaded ? 'hide' : ''}`}>
        <em>{String(step + 1).padStart(2, '0')}</em> — {String(STEPS.length).padStart(2, '0')}
      </div>

      {/* touch-action:none while locked — the browser never even STARTS a
          native touch scroll from the stage (including swipes that begin on
          the CTA buttons, which the Observer's ignore-list lets through).
          Restored in free-scroll and reduced-motion modes. */}
      <div ref={stageRef} className="tpc-stage" style={{ touchAction: free || prefersReducedMotion() ? 'auto' : 'none' }}>
        {STEPS.map((sc, i) => renderStep(sc, i))}
      </div>

      {/* Normal scroll resumes after the journey releases. */}
      <div className="tpc-after">
        {/* The trust strip that opened this section was removed — it repeated
            the shipping/payment/turnaround facts already stated under the
            closing CTA, where they actually influence the decision. */}

        {/* Light band — packaging paper. Opens with the marquee brand line,
            then the anatomy of a scent as three airy, hairline-ruled columns. */}
        <div className="tpc-band-light">
          <div className="tpc-marquee" aria-hidden="true">
            <div className="tpc-marquee-track">
              {[0, 1].flatMap((d) => MARQUEE.map((m, i) => <span key={`${d}-${i}`}>{m}</span>))}
            </div>
          </div>
          <section className="tpc-notes-sec">
            <p className="tpc-sec-eyebrow" data-reveal>Anatomy of a Scent</p>
            <h2 className="tpc-sec-title" data-reveal>Three acts. One signature.</h2>
            <div className="tpc-notes-grid">
              {NOTES.map((n) => (
                <div key={n.tag} className="tpc-note-col" data-reveal>
                  <span className="tpc-note-tag"><OptIcon name={n.icon} />{n.tag}</span>
                  <h3>{n.t}</h3>
                  <p>{n.d}</p>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* How it works — big numbered rows, editorial and left-aligned. */}
        <section className="tpc-howsec">
          <p className="tpc-sec-eyebrow left" data-reveal>Personality Mapping</p>
          <h2 className="tpc-sec-title left" data-reveal>From your story<br />to your formula.</h2>
          <div>
            {HOW_STEPS.map((h) => (
              <div key={h.t} className="tpc-how-row" data-reveal>
                <div className="tpc-how-body">
                  <h3>{h.t}</h3>
                  <p>{h.d}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Reasons to order now — sits between the explanation and the products,
            where someone who has just understood the process decides.
            Its cards arrive with two fetches, so the band's height settles after
            the reveal ScrollTriggers below it were measured — re-measure once it
            reports ready, or every section under it reveals at the wrong scroll
            position. */}
        <OffersHighlight onReady={() => ScrollTrigger.refresh()} />

        {/* The catalogue, buyable in place — see CollectionSection. Which
            products appear is set per-product in the admin panel, and the whole
            band is absent when none is flagged. Code-split and mounted on
            approach; the editorial "From Atelier to You" rail that used to sit
            above it was removed as a duplicate of this one. */}
        <DeferredCollection onReady={() => ScrollTrigger.refresh()} />

        {/* The difference — the honest hard-sell. No fabricated reviews; the
            two columns state exactly what mass perfume is and what this is. */}
        <div className="tpc-band-light">
          <section className="tpc-compare">
            <p className="tpc-sec-eyebrow" data-reveal>The Difference</p>
            <h2 className="tpc-sec-title" data-reveal>A scent that is only, and entirely, yours.</h2>
            <div className="tpc-cmp-grid">
              {COMPARE.map((c) => (
                <div key={c.tag} className={`tpc-cmp-col${c.mark === '✦' ? ' tpc-cmp-us' : ''}`} data-reveal>
                  <span className="tpc-cmp-tag">{c.tag}</span>
                  <ul>
                    {c.points.map((p) => (
                      <li key={p}><em>{c.mark}</em>{p}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* Practical details — answers the questions that stop a first order,
            nothing more. Rows stagger in on first scroll-in. */}
        <PracticalDetails />

        {/* What they say — the last thing read before the ask. No Suspense
            fallback: the band carries its own skeleton, and renders nothing at
            all when no testimonial is active, so a placeholder here would
            reserve space for something that may never arrive. */}
        <Suspense fallback={null}>
          <Testimonials onReady={() => ScrollTrigger.refresh()} />
        </Suspense>

        <section className="tpc-ctaband">
          <h2 data-reveal>Stop smelling like<br />everyone else.</h2>
          <p data-reveal>Less time than choosing off a shelf. Infinitely more you.</p>
          {/* line-height:0 wrapper — see .tpc-cta-line: keeps the inline-block
              button from adding descender space below itself, so the gap down
              to the trust line is exactly its margin. */}
          <div className="tpc-cta-line">
            <button className="tpc-begin" data-reveal onClick={() => craft('footer')}>Begin your composition</button>
          </div>
          {/* Separators are drawn by CSS, so they can drop out when the facts
              stack on narrow screens. */}
          <p className="tpc-cta-trust" data-reveal>
            <span>Free shipping across India</span>
            <span>Secure online payment</span>
            <span>Ships in 7-10 working days</span>
          </p>
        </section>

        <footer className="tpc-footer">
          <div className="tpc-footer-grid">
            <div className="tpc-foot-brand" data-reveal>
              <h3 className="tpc-foot-name">The Perfume Culture</h3>
              <p className="tpc-foot-sub">Fragrance as an extension of who you are — personality-mapped, composed and blended to order in India.</p>
            </div>
            <div className="tpc-foot-col" data-reveal>
              <h4>Explore</h4>
              <Link to="/shop">Shop</Link>
              {/* Same ask as the three buttons above, so it reports too — but
                  under its own name: a sitemap link that gets pressed means
                  something quite different from the closing CTA getting pressed. */}
              <Link to="/craft" onClick={() => trackCtaClick('footer_nav')}>Craft your scent</Link>
              <Link to="/orders">Your orders</Link>
            </div>
            <div className="tpc-foot-col" data-reveal>
              <h4>Company</h4>
              <Link to="/about">About us</Link>
              <Link to="/contact">Contact</Link>
            </div>
            <div className="tpc-foot-col" data-reveal>
              <h4>Legal</h4>
              <Link to="/terms">Terms &amp; Conditions</Link>
              <Link to="/privacy">Privacy Policy</Link>
              <Link to="/refund">Refund Policy</Link>
            </div>
            <div className="tpc-foot-col" data-reveal>
              <h4>Account</h4>
              <Link to="/login">Sign in</Link>
              <Link to="/signup">Create account</Link>
              <Link to="/profile">Profile</Link>
            </div>
          </div>
          <div className="tpc-foot-bottom">
            <span className="tpc-foot-copy">© {new Date().getFullYear()} The Perfume Culture · Crafted in India</span>
            <div className="tpc-foot-socials">
              {/* href unchanged — only the label became an icon. */}
              <a
                href="https://www.instagram.com/theperfumeculture.io/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="The Perfume Culture on Instagram"
                title="Instagram"
              >
                <InstagramIcon />
              </a>
            </div>
          </div>
        </footer>
      </div>
    </div>
  )
}
